import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CTU_SPINOFF_COLOR, CTU_STARTUP_COLOR } from '../../lib/colors';
import type { CtuCompany } from '../../types/api';
import JointOutcomesSheet from './JointOutcomesSheet';

interface CTUCompaniesTableProps {
  companies: CtuCompany[];
}

type SortKey = 'name' | 'web' | 'faculty' | 'year' | 'type';
type SortDir = 'asc' | 'desc';

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function sortValue(company: CtuCompany, key: SortKey): string | number {
  switch (key) {
    case 'name':
      return company.name.toLowerCase();
    case 'web':
      return company.web ? hostnameOf(company.web).toLowerCase() : '';
    case 'faculty':
      return (company.department?.faculty_abbr || '').toLowerCase();
    case 'year':
      return company.year ?? Number.NEGATIVE_INFINITY;
    case 'type':
      return company.type;
  }
}

const headCellClass =
  'px-3 py-2.5 text-left font-bold text-xs text-ctu-text-secondary whitespace-nowrap cursor-pointer select-none hover:bg-ctu-bg/70';

const sectionLabelClass =
  'text-[11px] font-bold text-ctu-text-muted mb-1.5 uppercase tracking-[0.14em]';

export default function CTUCompaniesTable({ companies }: CTUCompaniesTableProps) {
  const { t } = useTranslation('ctuCompanies');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('year');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [openSheetFor, setOpenSheetFor] = useState<CtuCompany | null>(null);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'year' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    const copy = [...companies];
    copy.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb));
      }
      if (cmp === 0) cmp = a.name.localeCompare(b.name);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [companies, sortKey, sortDir]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const sortIndicator = (key: SortKey) => {
    if (key !== sortKey) return <span className="ml-1 text-ctu-text-muted/40">↕</span>;
    return (
      <span className="ml-1 text-ctu-text-secondary">{sortDir === 'asc' ? '▲' : '▼'}</span>
    );
  };

  if (sorted.length === 0) {
    return (
      <Card className="border-ctu-border shadow-none">
        <CardContent className="py-10 text-center text-sm text-ctu-text-muted">
          {t('table.empty')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-ctu-border shadow-none">
      <CardContent className="px-0 pt-4 pb-2">
        <div className="px-6 mb-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('table.title')}
          </span>
        </div>
        <div className="overflow-x-auto">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-ctu-bg">
                <TableHead className={headCellClass} onClick={() => handleSort('name')}>
                  {t('table.name')}{sortIndicator('name')}
                </TableHead>
                <TableHead className={headCellClass} onClick={() => handleSort('web')}>
                  {t('table.web')}{sortIndicator('web')}
                </TableHead>
                <TableHead className={headCellClass} onClick={() => handleSort('faculty')}>
                  {t('table.department')}{sortIndicator('faculty')}
                </TableHead>
                <TableHead
                  className={`${headCellClass} text-right w-20`}
                  onClick={() => handleSort('year')}
                >
                  {t('table.year')}{sortIndicator('year')}
                </TableHead>
                <TableHead
                  className={`${headCellClass} w-28`}
                  onClick={() => handleSort('type')}
                >
                  {t('table.type')}{sortIndicator('type')}
                </TableHead>
                <TableHead className="px-3 py-2.5 w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((company) => {
                const isExpanded = expanded.has(company.id);
                const facultyAbbr = company.department?.faculty_abbr || null;
                const facultyLabel =
                  facultyAbbr ||
                  (company.department ? `#${company.department.id}` : '-');
                const typeColor =
                  company.type === 'Spin-off' ? CTU_SPINOFF_COLOR : CTU_STARTUP_COLOR;
                const typeLabel =
                  company.type === 'Spin-off' ? t('chart.spinoff') : t('chart.startup');

                return (
                  <React.Fragment key={company.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => toggle(company.id)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {company.name}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {company.web ? (
                          <a
                            href={company.web}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {hostnameOf(company.web)}
                          </a>
                        ) : (
                          <span className="text-ctu-text-muted">{t('table.noWeb')}</span>
                        )}
                      </TableCell>
                      <TableCell
                        className="text-foreground"
                        title={
                          company.department?.department ||
                          company.department?.faculty ||
                          undefined
                        }
                      >
                        {facultyLabel}
                      </TableCell>
                      <TableCell className="text-foreground text-right tabular-nums">
                        {company.year ?? '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className="text-white" style={{ backgroundColor: typeColor }}>
                          {typeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground text-center">
                        <span
                          className={`inline-block transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                          aria-hidden
                        >
                          &#9660;
                        </span>
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="px-6 py-5 bg-muted/40 border-b border-ctu-border"
                        >
                          {company.description && (
                            <div className="mb-4">
                              <div className={sectionLabelClass}>
                                {t('table.description')}
                              </div>
                              <div className="text-sm text-foreground leading-relaxed">
                                {company.description}
                              </div>
                            </div>
                          )}

                          {company.founders.length > 0 && (
                            <div className="mb-4">
                              <div className={sectionLabelClass}>
                                {t('table.founders')}
                              </div>
                              <ul className="flex flex-col gap-1 text-sm list-none p-0 m-0">
                                {company.founders.map((f) => (
                                  <li key={f.id}>
                                    <a
                                      href={`/person/${f.id}?from=ctu-companies`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-primary underline-offset-2 hover:underline"
                                    >
                                      {f.name || `#${f.id}`}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {company.justice && (
                            <div className="mb-4">
                              <div className={sectionLabelClass}>
                                {t('table.justice')}
                              </div>
                              <a
                                href={company.justice}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary text-sm underline-offset-2 hover:underline break-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {hostnameOf(company.justice)}
                              </a>
                            </div>
                          )}

                          {company.founders.length >= 2 && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={company.joint_outcomes.length === 0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenSheetFor(company);
                              }}
                            >
                              {t('table.showJointOutcomes', { count: company.joint_outcomes.length })}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <JointOutcomesSheet
        open={openSheetFor !== null}
        onOpenChange={(open) => {
          if (!open) setOpenSheetFor(null);
        }}
        companyName={openSheetFor?.name ?? ''}
        outcomes={openSheetFor?.joint_outcomes ?? []}
      />
    </Card>
  );
}
